// src/pages/Competitions/components/CompetitionCard.jsx

import React, { useMemo } from "react";
import Button from "../../../components/Button/Button";
import {
  FaPen,
  FaSlidersH,
  FaTrash,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaGlobe,
  FaTrophy,
  FaQrcode,
  FaDownload,
  FaLink,
  FaArrowRight,
  FaRedoAlt,
  FaUserPlus,
  FaCopy,
} from "react-icons/fa";

const FALLBACK_IMAGE = "/img/competition_default.jpg";

const QR_VISIBLE_SIZE = 136;
const QR_DOWNLOAD_SIZE = 768;

const parseDisciplines = (raw) => {
  try {
    const list = Array.isArray(raw) ? raw : JSON.parse(raw || '["Judo"]');
    return Array.isArray(list) && list.length ? list : ["Judo"];
  } catch {
    return ["Judo"];
  }
};

const formatDate = (value) => {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleDateString("fr-FR");
  } catch {
    return "—";
  }
};

const InfoPill = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-slate-200/70 bg-white px-3 py-2 shadow-sm dark:border-slate-700/70 dark:bg-slate-900/60">
    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
      <Icon className="text-slate-500 dark:text-slate-400" />
      {label}
    </div>
    <div className="mt-1 truncate text-sm font-semibold text-slate-800 dark:text-slate-100">
      {value || "—"}
    </div>
  </div>
);

const QrBox = ({ title, subtitle, icon: Icon, children, tone = "slate" }) => {
  const toneClasses =
    tone === "emerald"
      ? "border-emerald-200 bg-emerald-50/80 dark:border-emerald-800/70 dark:bg-emerald-950/20"
      : "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60";

  const iconClasses =
    tone === "emerald"
      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200"
      : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200";

  return (
    <div className={`rounded-2xl border p-3 shadow-sm ${toneClasses}`}>
      <div className="mb-3 flex items-start gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${iconClasses}`}
        >
          <Icon />
        </div>

        <div>
          <h4 className="text-sm font-bold text-slate-900 dark:text-white">
            {title}
          </h4>
          {subtitle && (
            <p className="mt-0.5 text-xs leading-snug text-slate-500 dark:text-slate-400">
              {subtitle}
            </p>
          )}
        </div>
      </div>

      {children}
    </div>
  );
};

const SmartQRCode = ({
  QRComponent,
  visibleId,
  downloadId,
  value,
  size = QR_VISIBLE_SIZE,
}) => {
  if (!QRComponent) return null;

  return (
    <div className="relative flex items-center justify-center">
      {/* QR visible dans la card */}
      <QRComponent
        id={visibleId}
        value={value}
        size={size}
        bgColor="#ffffff"
        fgColor="#000000"
        level="H"
        includeMargin
      />

      {/* QR haute qualité caché, utilisé uniquement pour le téléchargement */}
      <div className="pointer-events-none absolute -left-[99999px] top-0 opacity-0">
        <QRComponent
          id={downloadId}
          value={value}
          size={QR_DOWNLOAD_SIZE}
          bgColor="#ffffff"
          fgColor="#000000"
          level="H"
          includeMargin
        />
      </div>
    </div>
  );
};

const CompetitionCard = ({
  competition,
  publicAppUrl,
  onEdit,
  onSettings,
  onDelete,
  onReset,
  canReset,
  onView,
  onCopyInvite,
  onCopyArrivalRegistration,
  onChangeClubInput,
  onDownloadQR,
  QRComponent,
}) => {
  const disciplineList = useMemo(
    () => parseDisciplines(competition.disciplines_competition),
    [competition.disciplines_competition],
  );

  const safeBaseUrl = (
    publicAppUrl ||
    (typeof window !== "undefined" ? window.location.origin : "") ||
    ""
  ).replace(/\/$/, "");

  const resultsQrVisibleId = `qr-results-visible-${competition.id}`;
  const resultsQrDownloadId = `qr-results-download-${competition.id}`;

  const arrivalQrVisibleId = `qr-arrival-registration-visible-${competition.id}`;
  const arrivalQrDownloadId = `qr-arrival-registration-download-${competition.id}`;

  const resultsUrl = `${safeBaseUrl}/resultats-public/${competition.id}`;
  const arrivalRegistrationUrl = `${safeBaseUrl}/inscription-judoka/${competition.id}`;

  const imageSrc = competition.photo_URL || FALLBACK_IMAGE;

  return (
    <article className="overflow-hidden rounded-[28px] border border-slate-200/80 bg-white shadow-sm transition hover:shadow-md dark:border-slate-700/80 dark:bg-slate-900">
      <div className="flex flex-col xl:flex-row">
        {/* Image */}
        <div className="relative min-h-[220px] xl:w-[340px] xl:min-h-full">
          <img
            src={imageSrc}
            alt={competition.nom_competition || "Compétition"}
            className="h-full min-h-[220px] w-full object-cover xl:absolute xl:inset-0"
            onError={(e) => {
              if (e.currentTarget.src !== FALLBACK_IMAGE) {
                e.currentTarget.src = FALLBACK_IMAGE;
              }
            }}
          />

          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent xl:bg-gradient-to-r xl:from-black/75 xl:via-black/25 xl:to-transparent" />

          <div className="absolute left-4 right-4 top-4 flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ring-1 ${
                competition.is_active
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                  : "bg-red-50 text-red-700 ring-red-200"
              }`}
            >
              {competition.is_active ? "Active" : "Inactive"}
            </span>

            <span
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold ring-1 ${
                competition.is_public
                  ? "bg-sky-50 text-sky-700 ring-sky-200"
                  : "bg-slate-50 text-slate-700 ring-slate-200"
              }`}
            >
              <FaGlobe />
              {competition.is_public ? "Publique" : "Privée"}
            </span>
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-5 text-white">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
              Compétition
            </p>
            <h3 className="mt-1 text-2xl font-bold leading-tight">
              {competition.nom_competition || "Compétition sans nom"}
            </h3>
            <p className="mt-2 flex items-center gap-2 text-sm text-white/80">
              <FaMapMarkerAlt />
              {competition.lieu_competition || "Lieu non renseigné"}
            </p>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex-1 p-5 lg:p-6">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <InfoPill
                icon={FaTrophy}
                label="Niveau"
                value={competition.level_competition || "—"}
              />

              <InfoPill
                icon={FaMapMarkerAlt}
                label="Lieu"
                value={competition.lieu_competition || "—"}
              />

              <InfoPill
                icon={FaCalendarAlt}
                label="Date"
                value={formatDate(competition.date_competition)}
              />

              <InfoPill
                icon={FaGlobe}
                label="Visibilité"
                value={competition.is_public ? "Publique" : "Privée"}
              />
            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-[1.25fr_0.75fr]">
              <div className="rounded-2xl border border-slate-200/70 bg-slate-50/70 p-4 dark:border-slate-700/70 dark:bg-slate-950/30">
                <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                  <FaClock className="text-slate-500" />
                  Horaires de pesées
                </div>

                <div
                  className="text-sm leading-relaxed text-slate-700 dark:text-slate-300
                  [&_ul]:list-disc [&_ul]:pl-5
                  [&_ol]:list-decimal [&_ol]:pl-5
                  [&_a]:underline
                  [&_strong]:font-bold"
                  dangerouslySetInnerHTML={{
                    __html:
                      competition.horaires_pesees ||
                      "<em>Aucun horaire renseigné.</em>",
                  }}
                />
              </div>

              <div className="rounded-2xl border border-slate-200/70 bg-slate-50/70 p-4 dark:border-slate-700/70 dark:bg-slate-950/30">
                <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-900 dark:text-white">
                  <FaTrophy className="text-slate-500" />
                  Disciplines
                </div>

                <div className="flex flex-wrap gap-2">
                  {disciplineList.map((discipline) => (
                    <span
                      key={discipline}
                      className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                    >
                      {discipline}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions admin */}
            <div className="mt-5 flex flex-wrap gap-2">
              <Button
                variant="soft"
                size="sm"
                className="rounded-xl"
                icon={FaPen}
                onClick={() => onEdit(competition.id)}
              >
                Éditer
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="rounded-xl"
                icon={FaSlidersH}
                onClick={() => onSettings(competition.id)}
              >
                Paramètres
              </Button>

              <Button
                variant="danger"
                size="sm"
                className="rounded-xl"
                icon={FaTrash}
                onClick={() =>
                  onDelete(competition.id, competition.nom_competition)
                }
              >
                Supprimer
              </Button>

              {canReset && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50 dark:border-amber-700/60 dark:text-amber-300 dark:hover:bg-amber-900/20"
                  icon={FaRedoAlt}
                  onClick={() =>
                    onReset(competition.id, competition.nom_competition)
                  }
                >
                  Reset Distributions, Combats, Classements
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Colonne QR / liens */}
        <aside className="border-t border-slate-200 bg-slate-50/80 p-5 dark:border-slate-700 dark:bg-slate-950/30 xl:w-[410px] xl:border-l xl:border-t-0">
          <Button
            variant="brand"
            size="md"
            fullWidth
            className="mb-4 rounded-2xl"
            icon={FaArrowRight}
            onClick={() => onView(competition.id)}
          >
            Accéder à la compétition
          </Button>

          <div className="grid gap-3">
            <QrBox
              title="Lien d’invitation Club"
              subtitle="Pour envoyer une inscription groupée à un responsable de club."
              icon={FaLink}
            >
              <input
                type="text"
                placeholder="Nom du club invité"
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-orange-400 focus:ring-4 focus:ring-orange-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:focus:ring-orange-950"
                value={competition.clubInput || ""}
                onChange={(e) =>
                  onChangeClubInput(competition.id, e.target.value)
                }
              />

              <Button
                variant="outline"
                size="sm"
                className="mt-2 w-full rounded-xl"
                icon={FaCopy}
                onClick={() => onCopyInvite(competition)}
              >
                Copier le lien club
              </Button>
            </QrBox>

            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1 2xl:grid-cols-2">
              {competition.is_public && (
                <QrBox
                  title="Résultats publics"
                  subtitle="QR code destiné au public."
                  icon={FaQrcode}
                >
                  <div className="flex flex-col items-center">
                    {QRComponent && (
                      <SmartQRCode
                        QRComponent={QRComponent}
                        visibleId={resultsQrVisibleId}
                        downloadId={resultsQrDownloadId}
                        value={resultsUrl}
                      />
                    )}

                    <Button
                      variant="outline"
                      size="xs"
                      className="mt-2 rounded-xl"
                      icon={FaDownload}
                      onClick={() =>
                        onDownloadQR(
                          resultsQrDownloadId,
                          `qr_resultats_competition_${competition.id}.png`,
                        )
                      }
                    >
                      Télécharger
                    </Button>
                  </div>
                </QrBox>
              )}

              <QrBox
                title="Inscription sur place"
                subtitle="QR code à afficher à l’accueil du tournoi."
                icon={FaUserPlus}
                tone="emerald"
              >
                <div className="flex flex-col items-center">
                  {QRComponent && (
                    <SmartQRCode
                      QRComponent={QRComponent}
                      visibleId={arrivalQrVisibleId}
                      downloadId={arrivalQrDownloadId}
                      value={arrivalRegistrationUrl}
                    />
                  )}

                  <div className="mt-2 grid w-full grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="xs"
                      className="rounded-xl bg-white/90 dark:bg-slate-900/70"
                      icon={FaDownload}
                      onClick={() =>
                        onDownloadQR(
                          arrivalQrDownloadId,
                          `qr_inscription_sur_place_${competition.id}.png`,
                        )
                      }
                    >
                      QR
                    </Button>

                    <Button
                      variant="outline"
                      size="xs"
                      className="rounded-xl bg-white/90 dark:bg-slate-900/70"
                      icon={FaCopy}
                      onClick={() => onCopyArrivalRegistration(competition)}
                    >
                      Lien
                    </Button>
                  </div>
                </div>
              </QrBox>
            </div>
          </div>
        </aside>
      </div>
    </article>
  );
};

export default CompetitionCard;
